import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export function top_animation() {
	const isPc = window.matchMedia('(min-width: 782px)').matches;

	/*------------------------------------------------
	ファーストビュー アニメーション
	------------------------------------------------*/
	(() => {
		const slider = document.querySelector('.p-top-fv-slider');
		const dragArea = slider ? slider.closest('.p-top-fv') : null;
		const slides = Array.from(document.querySelectorAll('.p-top-fv-slider__item'));

		if (!slider || !dragArea || slides.length === 0) {
			return;
		}

		const dragMedia = slider.querySelectorAll('img, video');
		const targetSpeed = (isPc ? 3 : 2) * 60;
		const speedObj = { current: 0 };

		let winWidth = window.innerWidth;
		let slideWidth = 0;
		let stepWidth = 0;
		let totalWidth = 0;
		let slideMarginLeft = 0;
		let parallaxMax = 0;

		let isDragging = false;
		let draggingPointerId = null;
		let dragStartX = 0;
		let dragStartY = 0;
		let dragLastX = 0;
		let dragLastTime = 0;
		let dragVelocity = 0;
		let dragSamples = [];
		let hasDragged = false;
		let isInertiaScrolling = false;
		let inertiaTween;

		const dragThreshold = 4;
		const inertiaMinVelocity = isPc ? 80 : 40;
		const inertiaSampleDuration = 180;
		const inertiaStaleDuration = isPc ? 120 : 260;

		dragMedia.forEach((media) => {
			media.draggable = false;
		});

		const getParallaxX = (x) => {
			let ratio = (x + slideMarginLeft + slideWidth) / (winWidth + slideWidth);
			ratio = Math.max(0, Math.min(1, ratio));

			return (0.5 - ratio) * parallaxMax * 2;
		};

		const renderSlide = (slide, x) => {
			gsap.set(slide, { x });

			const parallaxX = getParallaxX(x);
			const imgWrap = slide.querySelector('.p-top-fv-slider__img');
			const videoWrap = slide.querySelector('.p-top-fv-slider__video');

			if (imgWrap) {
				gsap.set(imgWrap, { x: parallaxX });
			}

			if (videoWrap) {
				gsap.set(videoWrap, { x: parallaxX });
			}
		};

		const normalizeSlideX = (x) => {
			if (totalWidth <= 0) {
				return x;
			}

			const minX = -(slideWidth + slideMarginLeft);
			const maxX = minX + totalWidth;

			while (x < minX) {
				x += totalWidth;
			}

			while (x >= maxX) {
				x -= totalWidth;
			}

			return x;
		};

		const moveSlides = (deltaX) => {
			slides.forEach((slide) => {
				const x = normalizeSlideX(gsap.getProperty(slide, 'x') + deltaX);
				renderSlide(slide, x);
			});
		};

		const setupLayout = () => {
			winWidth = window.innerWidth;
			slideWidth = slides[0].offsetWidth;

			if (slideWidth <= 0) {
				return;
			}

			parallaxMax = slideWidth * 0.15;

			const style = window.getComputedStyle(slides[0]);
			slideMarginLeft = parseFloat(style.marginLeft) || 0;
			const marginRight = parseFloat(style.marginRight) || 0;

			stepWidth = slideWidth + slideMarginLeft + marginRight;
			totalWidth = slides.length * stepWidth;

			const offsetX = (winWidth - slideWidth) / 2 - slideMarginLeft;

			slides.forEach((slide, index) => {
				const x = offsetX + (index - 1) * stepWidth;
				renderSlide(slide, x);
			});
		};

		setupLayout();

		let resizeTimeout;
		let lastWinWidth = window.innerWidth;

		window.addEventListener('resize', () => {
			const currentWinWidth = window.innerWidth;

			if (currentWinWidth === lastWinWidth) {
				return;
			}

			lastWinWidth = currentWinWidth;
			clearTimeout(resizeTimeout);
			resizeTimeout = setTimeout(setupLayout, 200);
		});

		const tickSlider = (_time, deltaTime) => {
			if (isDragging || isInertiaScrolling) {
				return;
			}

			const deltaSecond = Math.min(deltaTime, 100) / 1000;
			moveSlides(-speedObj.current * deltaSecond);
		};

		const resumeAutoSlide = () => {
			gsap.to(speedObj, {
				current: targetSpeed,
				duration: 0.8,
				ease: 'none',
				overwrite: true,
			});
		};

		const stopInertia = () => {
			if (inertiaTween) {
				inertiaTween.kill();
				inertiaTween = null;
			}

			isInertiaScrolling = false;
		};

		const startInertia = (velocity) => {
			if (!Number.isFinite(velocity) || Math.abs(velocity) < inertiaMinVelocity) {
				resumeAutoSlide();
				return;
			}

			const proxy = { x: 0 };
			let lastX = 0;

			isInertiaScrolling = true;

			inertiaTween = gsap.to(proxy, {
				x: velocity * 0.35,
				duration: Math.min(Math.max(Math.abs(velocity) / 1600, 0.25), 0.9),
				ease: 'power2.out',
				onUpdate: () => {
					const x = proxy.x;
					moveSlides(x - lastX);
					lastX = x;
				},
				onComplete: () => {
					isInertiaScrolling = false;
					inertiaTween = null;
					resumeAutoSlide();
				},
			});
		};

		const addDragSample = (x, time) => {
			dragSamples.push({ x, time });
			dragSamples = dragSamples.filter((sample) => time - sample.time <= inertiaSampleDuration);
		};

		const getReleaseVelocity = () => {
			const now = performance.now();

			if (now - dragLastTime > inertiaStaleDuration) {
				return 0;
			}

			const firstSample = dragSamples[0];
			const lastSample = dragSamples[dragSamples.length - 1];

			if (!firstSample || !lastSample || firstSample === lastSample) {
				return dragVelocity;
			}

			const elapsed = Math.max((lastSample.time - firstSample.time) / 1000, 0.016);
			return (lastSample.x - firstSample.x) / elapsed;
		};

		const startDrag = (id, x, y) => {
			stopInertia();

			isDragging = true;
			draggingPointerId = id;
			dragStartX = x;
			dragStartY = y;
			dragLastX = x;
			dragLastTime = performance.now();
			dragVelocity = 0;
			dragSamples = [{ x, time: dragLastTime }];
			hasDragged = false;

			slider.classList.add('is-dragging');

			gsap.to(speedObj, {
				current: 0,
				duration: 0.25,
				overwrite: true,
			});
		};

		const preventTouchScroll = (event) => {
			if (event.cancelable) {
				event.preventDefault();
			}
		};

		const updateDrag = (id, x, y, event) => {
			if (!isDragging || id !== draggingPointerId) {
				return;
			}

			const now = performance.now();
			const elapsed = Math.max((now - dragLastTime) / 1000, 0.016);
			const deltaX = x - dragLastX;
			const totalDeltaX = x - dragStartX;
			const totalDeltaY = y - dragStartY;

			dragLastX = x;
			dragLastTime = now;
			addDragSample(x, now);

			if (!hasDragged && Math.abs(totalDeltaX) > dragThreshold) {
				if (Math.abs(totalDeltaX) > Math.abs(totalDeltaY)) {
					hasDragged = true;
				} else if (Math.abs(totalDeltaY) > dragThreshold) {
					endDrag(id);
					return;
				}
			}

			if (!hasDragged) {
				return;
			}

			preventTouchScroll(event);
			moveSlides(deltaX);
			dragVelocity = deltaX / elapsed;
		};

		const endDrag = (id, event) => {
			if (!isDragging || id !== draggingPointerId) {
				return;
			}

			const releaseVelocity = getReleaseVelocity();
			const shouldStartInertia = hasDragged && Math.abs(releaseVelocity) >= inertiaMinVelocity;

			isDragging = false;
			draggingPointerId = null;
			dragSamples = [];

			slider.classList.remove('is-dragging');

			if (event && event.pointerId !== undefined && dragArea.releasePointerCapture) {
				try {
					dragArea.releasePointerCapture(event.pointerId);
				} catch {}
			}

			if (shouldStartInertia) {
				startInertia(releaseVelocity);
			} else {
				resumeAutoSlide();
			}
		};

		const onPointerDown = (event) => {
			if (event.pointerType === 'touch') {
				return;
			}

			if (event.pointerType === 'mouse' && event.button !== 0) {
				return;
			}

			if (event.target.closest?.('a, button, input, textarea, select, [data-no-drag]')) {
				return;
			}

			startDrag(event.pointerId, event.clientX, event.clientY);

			if (dragArea.setPointerCapture) {
				dragArea.setPointerCapture(event.pointerId);
			}
		};

		const onPointerMove = (event) => {
			updateDrag(event.pointerId, event.clientX, event.clientY, event);
		};

		const onPointerEnd = (event) => {
			endDrag(event.pointerId, event);
		};

		const getActiveTouch = (event) =>
			Array.from(event.changedTouches).find((touch) => touch.identifier === draggingPointerId);

		const onTouchStart = (event) => {
			if (event.touches.length !== 1) {
				return;
			}

			if (event.target.closest?.('a, button, input, textarea, select, [data-no-drag]')) {
				return;
			}

			const touch = event.touches[0];
			startDrag(touch.identifier, touch.clientX, touch.clientY);
		};

		const onTouchMove = (event) => {
			const touch = getActiveTouch(event);

			if (!touch) {
				return;
			}

			updateDrag(touch.identifier, touch.clientX, touch.clientY, event);
		};

		const onTouchEnd = (event) => {
			const touch = getActiveTouch(event);

			if (!touch) {
				return;
			}

			endDrag(touch.identifier);
		};

		dragArea.addEventListener('pointerdown', onPointerDown);
		dragArea.addEventListener('pointermove', onPointerMove);
		dragArea.addEventListener('pointerup', onPointerEnd);
		dragArea.addEventListener('pointercancel', onPointerEnd);
		dragArea.addEventListener('lostpointercapture', onPointerEnd);
		dragArea.addEventListener('touchstart', onTouchStart, { passive: true });
		dragArea.addEventListener('touchmove', onTouchMove, { passive: false });
		dragArea.addEventListener('touchend', onTouchEnd);
		dragArea.addEventListener('touchcancel', onTouchEnd);
		dragArea.addEventListener('dragstart', (event) => {
			event.preventDefault();
		});

		window.addEventListener('load', () => {
			const videos = document.querySelectorAll('.p-top-fv-slider__video video');

			videos.forEach((video) => {
				const src = video.getAttribute('data-top-src');

				if (src) {
					video.src = src;
					video.load();
				}
			});
		});

		const playVideosAndHideThumbs = () => {
			const videos = document.querySelectorAll('.p-top-fv-slider__video video');

			videos.forEach((video) => {
				const playPromise = video.play();

				if (playPromise === undefined) {
					return;
				}

				playPromise
					.then(() => {
						const parentItem = video.closest('.p-top-fv-slider__item');
						const thumbWrap = parentItem ? parentItem.querySelector('.p-top-fv-slider__img') : null;

						if (thumbWrap) {
							gsap.to(thumbWrap, {
								autoAlpha: 0,
								duration: 0.8,
								ease: 'power2.inOut',
							});
						}
					})
					.catch((error) => {
						console.warn('自動再生がブロックされました:', error);
					});
			});
		};

		window.addEventListener('load', () => {
			const fv = document.querySelector('.p-top-fv');
			const title = document.querySelector('.p-top-fv-ttl');
			const titleImages = document.querySelectorAll('.p-top-fv-ttl__img img');
			const copyImages = document.querySelectorAll('.p-top-fv-copy__txt img');

			if (!fv || !title) {
				gsap.ticker.add(tickSlider);
				resumeAutoSlide();
				playVideosAndHideThumbs();
				return;
			}

			const mainTl = gsap.timeline({
				defaults: { ease: 'power3.inOut' },
				onComplete: () => {
					playVideosAndHideThumbs();
				},
			});

			if (titleImages.length > 0) {
				mainTl.to(titleImages, {
					x: 0,
					duration: 1,
				});
			}

			const containerRect = fv.getBoundingClientRect();
			const titleRect = title.getBoundingClientRect();

			const containerCenterX = containerRect.left + containerRect.width / 2;
			const containerCenterY = containerRect.top + containerRect.height / 2;

			const titleCenterX = titleRect.left + titleRect.width / 2;
			const titleCenterY = titleRect.top + titleRect.height / 2;

			const xOffset = containerCenterX - titleCenterX;
			const yOffset = containerCenterY - titleCenterY;

			mainTl.from(
				title,
				{
					x: xOffset,
					y: yOffset,
					duration: 1,
					clearProps: 'all',
				},
				'>+0.3',
			);

			if (copyImages.length > 0) {
				mainTl.to(
					copyImages,
					{
						x: 0,
						duration: 1,
						stagger: 0.05,
					},
					'>-.9',
				);
			}

			mainTl.call(
				() => {
					gsap.ticker.add(tickSlider);
				},
				null,
				'<',
			);

			mainTl.to(
				speedObj,
				{
					current: targetSpeed,
					duration: 1.6,
					ease: 'power2.in',
				},
				'<',
			);
		});
	})();

	/*------------------------------------------------
	豊安工業について アニメーション
	------------------------------------------------*/
	(() => {
		const gallery = document.querySelector('.p-top-about-gallery');
		const images = document.querySelectorAll('.p-top-about-gallery__img, .p-top-about-gallery__en');

		if (!gallery || images.length === 0) {
			return;
		}

		gsap.to(images, {
			clipPath: 'inset(0% 0% 0% 0%)',
			duration: 1,
			ease: 'power3.inOut',
			stagger: 0.1,
			scrollTrigger: {
				trigger: gallery,
				start: isPc ? 'top center' : 'top bottom-=10%',
				once: true,
			},
		});
	})();

	/*------------------------------------------------
	サポート アニメーション
	------------------------------------------------*/
	(() => {
		const support = document.querySelector('.p-top-support-cnt');
		const maskSp = document.querySelector('.p-top-support-cnt-stroke__sp .p-top-support-cnt-stroke__mask');
		const maskPc = document.querySelector('.p-top-support-cnt-stroke__pc .p-top-support-cnt-stroke__mask');
		const supportItems = document.querySelectorAll('.p-top-support-cnt-list__item');
		const floor = document.querySelector('.p-top-support-cnt__floor');

		if (!support || !maskSp || !maskPc || supportItems.length === 0 || !floor) {
			return;
		}

		const lineDuration = 5;
		const maskSpLength = maskSp.getTotalLength();
		const maskPcLength = maskPc.getTotalLength();

		const lineSteps = [
			{ progress: { SP: 0.16, PC: 0.28 } },
			{ progress: { SP: 0.32, PC: 0.39 } },
			{ progress: { SP: 0.5, PC: 0.5 } },
			{ progress: { SP: 0.66, PC: 0.78 } },
			{ progress: { SP: 0.82, PC: 0.89 } },
			{ progress: { SP: 1, PC: 1 } },
		];

		gsap.set(maskSp, {
			strokeDasharray: maskSpLength,
			strokeDashoffset: maskSpLength,
		});

		gsap.set(maskPc, {
			strokeDasharray: maskPcLength,
			strokeDashoffset: maskPcLength,
		});

		gsap.set(supportItems, {
			transformOrigin: 'center center',
		});

		const supportLineTl = gsap.timeline({
			paused: true,
			defaults: { ease: 'none' },
		});

		const resetSupportLine = () => {
			supportLineTl.pause(0, true);

			supportItems.forEach((item) => {
				item.classList.remove('is-active', 'is-illust-active');
			});

			gsap.set(maskSp, {
				strokeDashoffset: maskSpLength,
			});

			gsap.set(maskPc, {
				strokeDashoffset: maskPcLength,
			});
		};

		lineSteps.forEach((step, index) => {
			const item = supportItems[index];

			if (!item) {
				return;
			}

			const duration = lineDuration / lineSteps.length;

			supportLineTl
				.call(
					() => {
						item.classList.add('is-active', 'is-illust-active');
					},
					null,
					index !== 0 ? '>-0.2' : undefined,
				)
				.to(maskSp, {
					strokeDashoffset: maskSpLength * (1 + step.progress.SP),
					duration,
				})
				.to(
					maskPc,
					{
						strokeDashoffset: maskPcLength * (1 - step.progress.PC),
						duration,
					},
					'<',
				);
		});

		supportLineTl.eventCallback('onComplete', () => {
			gsap.delayedCall(1, () => {
				resetSupportLine();

				gsap.delayedCall(1, () => {
					supportLineTl.play(0);
				});
			});
		});

		const enterTl = gsap.timeline({
			scrollTrigger: {
				trigger: support,
				start: 'top bottom-=20%',
				once: true,
			},
		});

		enterTl.from(floor, {
			yPercent: -30,
			duration: 0.8,
			ease: 'elastic.out(0.5,0.5)',
		});

		enterTl.from(
			floor,
			{
				opacity: 0,
			},
			'<',
		);

		enterTl.from(
			supportItems,
			{
				yPercent: 30,
				opacity: 0,
				stagger: 0.05,
				ease: 'power4.out',
				clearProps: 'all',
				duration: 1,
			},
			'>-0.1',
		);

		enterTl.call(
			() => {
				supportLineTl.play();
			},
			null,
			'>-1',
		);
	})();

	/*------------------------------------------------
	スライダー
	------------------------------------------------*/
	(() => {
		const slider = document.querySelector('.p-top-slider');
		const track = slider?.querySelector('.p-top-slider__track');

		if (!slider || !track) {
			return;
		}

		const speed = 50;

		let ticker;
		let loopWidth = 0;
		let currentX = 0;
		let resizeTimer;

		const removeClones = () => {
			track.querySelectorAll('[data-slider-clone="true"]').forEach((clone) => {
				clone.remove();
			});
		};

		const getOriginalSlides = () =>
			Array.from(track.children).filter((slide) => slide.dataset.sliderClone !== 'true');

		const wrapX = (x) => gsap.utils.wrap(-loopWidth, 0, x);

		const render = () => {
			currentX = wrapX(currentX);
			gsap.set(track, { x: currentX });
		};

		const setupSlider = () => {
			if (ticker) {
				gsap.ticker.remove(ticker);
			}

			removeClones();

			currentX = 0;
			gsap.set(track, { x: currentX });

			const slides = getOriginalSlides();

			if (slides.length === 0) {
				return;
			}

			loopWidth = slides.reduce((width, slide) => width + slide.getBoundingClientRect().width, 0);

			if (loopWidth <= 0) {
				return;
			}

			while (track.scrollWidth < loopWidth + slider.clientWidth) {
				slides.forEach((slide) => {
					const clone = slide.cloneNode(true);
					clone.dataset.sliderClone = 'true';
					clone.setAttribute('aria-hidden', 'true');
					track.appendChild(clone);
				});
			}

			ticker = (_time, deltaTime) => {
				currentX -= speed * (deltaTime / 1000);
				render();
			};

			gsap.ticker.add(ticker);
		};

		setupSlider();

		window.addEventListener('load', setupSlider, {
			once: true,
		});

		let windowWidth = window.innerWidth;

		window.addEventListener('resize', () => {
			const currentWidth = window.innerWidth;

			if (currentWidth === windowWidth) {
				return;
			}

			windowWidth = currentWidth;

			clearTimeout(resizeTimer);
			resizeTimer = setTimeout(setupSlider, 200);
		});
	})();
}